import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useCallback } from "react";
import { createMarketplaceListing, getMarketplaceListing, updateMarketplaceListing, uploadMarketplaceImage, getPublicImageUrl, getMarketplaceCategories, type Category } from "@/lib/marketplaceAPI";
import { Upload, Plus, X, Image, ImagePlus, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import type { Category as CategorySchema } from "../lib/schema";

// Esquema de validación para el formulario
const usedProductSchema = z.object({
  title: z.string().min(3, "El título debe tener al menos 3 caracteres"),
  description: z.string().min(10, "La descripción debe tener al menos 10 caracteres"),
  price: z.number().min(1, "El precio debe ser mayor que 0"),
  brand: z.string().min(1, "La marca es obligatoria"),
  condition: z.string(),
  category_id: z.string().min(1, "La categoría es obligatoria"),
  location: z.string().optional(),
  year: z.number().nullable().optional(),
  features: z.array(z.string()),
  technical_specs: z.array(z.object({
    label: z.string(),
    value: z.string()
  })),
  images: z.array(z.string()),
  is_active: z.boolean()
});

type UsedProductFormValues = z.infer<typeof usedProductSchema>;

// Interfaz para las props del componente
interface AdminUsedProductFormProps {
  productId: string | null;
  onComplete: () => void;
}

// Interfaz para representar una imagen local
interface LocalImage {
  file: File;
  preview: string;
  uploading: boolean;
  error?: string;
}

export default function AdminUsedProductForm({ productId, onComplete }: AdminUsedProductFormProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Estado para las imágenes
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // --- Añadir Query para cargar categorías ---
  const { data: categories, isLoading: isLoadingCategories } = useQuery<CategorySchema[]>({
    queryKey: ["marketplace-categories-form"],
    queryFn: getMarketplaceCategories,
    staleTime: Infinity, // Las categorías no cambian a menudo
  });
  // --- Fin de Query para categorías ---

  // Formulario con valores por defecto
  const form = useForm<UsedProductFormValues>({
    resolver: zodResolver(usedProductSchema),
    defaultValues: {
      title: "",
      description: "",
      price: 0,
      brand: "",
      condition: "good",
      category_id: "",
      location: "",
      year: null,
      features: [],
      technical_specs: [],
      images: [],
      is_active: true
    },
  });

  // Configuración del dropzone para arrastrar y soltar imágenes
  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Crear previsualizaciones para los archivos aceptados
    const newLocalImages = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false
    }));

    setLocalImages(prev => [...prev, ...newLocalImages]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize: 5242880, // 5MB
    maxFiles: 10
  });

  // Si estamos editando, obtener los datos del producto
  const { data: productData, isLoading: isLoadingProduct, isError, error } = useQuery({
    queryKey: ["marketplace-listing", productId],
    queryFn: () => productId ? getMarketplaceListing(productId) : Promise.resolve(null),
    enabled: !!productId,
    staleTime: 0 // Siempre obtener datos frescos
  });

  // Actualizar el formulario con los datos del producto cuando los tengamos
  useEffect(() => {
    // Log fuera del 'if' para ver si se ejecuta y el estado
    console.log(
      "useEffect para resetear formulario ejecutado.",
      { productId, isLoadingProduct, isError, error, productData }
    );

    if (productData) {
      // --- Añadir log para depuración ---
      console.log("Datos del producto recibidos (dentro del if), intentando resetear formulario:", productData);
      // --- Fin del log ---
      form.reset({
        title: productData.title,
        description: productData.description,
        price: productData.price,
        brand: productData.brand,
        condition: productData.condition,
        // Asegurarse de que category_id se pasa como string
        category_id: String(productData.category_id),
        location: productData.location || "",
        year: productData.year || null,
        features: productData.features || [],
        technical_specs: productData.technical_specs || [],
        images: productData.images || [],
        is_active: productData.is_active
      });

      // Actualizar las previsualizaciones de imágenes
      setImagePreviews(productData.images || []);
    }
  // Añadir productId a las dependencias
  }, [productData, productId, isError, error, isLoadingProduct]); // Añadimos más dependencias para asegurar ejecución

  // Limpiar las URLs de objeto cuando el componente se desmonte
  useEffect(() => {
    return () => {
      localImages.forEach(image => {
        URL.revokeObjectURL(image.preview);
      });
    };
  }, [localImages]);

  // Función para subir una imagen
  const handleImageUpload = async (file: File): Promise<string | null> => {
    try {
      // Marcar la imagen como subiendo
      setLocalImages(images =>
        images.map(img =>
          img.file === file ? { ...img, uploading: true, error: undefined } : img
        )
      );

      // Subir la imagen usando la función de la API
      const filePath = await uploadMarketplaceImage(file);

      // Obtener la URL pública
      const publicUrl = getPublicImageUrl(filePath);

      // Marcar la imagen como subida correctamente
      setLocalImages(images =>
        images.map(img =>
          img.file === file ? { ...img, uploading: false } : img
        )
      );

      return filePath; // Devolver la ruta del archivo (no la URL completa)
    } catch (error) {
      console.error('Error al subir imagen:', error);

      // Marcar la imagen con error
      setLocalImages(images =>
        images.map(img =>
          img.file === file ? { ...img, uploading: false, error: error instanceof Error ? error.message : 'Error al subir' } : img
        )
      );

      toast({
        title: "Error al subir imagen",
        description: error instanceof Error ? error.message : "No se pudo subir la imagen",
        variant: "destructive"
      });

      return null;
    }
  };

  // Función para subir todas las imágenes locales
  const uploadAllImages = async (): Promise<string[]> => {
    if (localImages.length === 0) {
      // Si no hay imágenes locales, solo devolver las URLs existentes
      return form.getValues("images");
    }

    setUploadingImages(true);
    const uploadedPaths: string[] = [];
    const currentPaths = [...form.getValues("images")];

    try {
      // Marcar todas las imágenes como en proceso de carga
      setLocalImages(images =>
        images.map(img => ({ ...img, uploading: true, error: undefined }))
      );

      // Subir cada imagen y guardar su ruta
      for (let i = 0; i < localImages.length; i++) {
        const image = localImages[i];

        // Evitar subir imágenes que ya tengan error
        if (image.error) continue;

        try {
          // Subir la imagen al servidor
          const filePath = await uploadMarketplaceImage(image.file);

          if (filePath) {
            uploadedPaths.push(filePath);
          }

          // Actualizar el estado de esta imagen
          setLocalImages(images =>
            images.map((img, index) =>
              i === index ? { ...img, uploading: false } : img
            )
          );
        } catch (error) {
          // Manejar error de carga individual
          setLocalImages(images =>
            images.map((img, index) =>
              i === index ? { ...img, uploading: false, error: 'Error al subir' } : img
            )
          );
          console.error("Error al subir imagen:", error);
        }
      }

      // Combinar las rutas existentes con las nuevas
      return [...currentPaths, ...uploadedPaths];
    } finally {
      setUploadingImages(false);
    }
  };

  // Mutación para crear un producto
  const createProductMutation = useMutation({
    mutationFn: async (data: UsedProductFormValues) => {
      // Primero subir todas las imágenes
      const allImagePaths = await uploadAllImages();

      // Luego crear el producto con todas las rutas
      return createMarketplaceListing({
        ...data,
        images: allImagePaths,
        technical_specs: data.technical_specs,
        year: data.year === null ? undefined : data.year // Corregir el tipo para year
      });
    },
    onSuccess: () => {
      toast({
        title: "Producto creado",
        description: "El producto usado se ha creado exitosamente"
      });
      queryClient.invalidateQueries({ queryKey: ["marketplace-used-listings"] });
      onComplete();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo crear el producto: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    }
  });

  // Mutación para actualizar un producto
  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UsedProductFormValues }) => {
      // Primero subir todas las imágenes
      const allImagePaths = await uploadAllImages();

      // Luego actualizar el producto con todas las rutas
      return updateMarketplaceListing(id, {
        ...data,
        images: allImagePaths,
        year: data.year === null ? undefined : data.year // Corregir el tipo para year
      });
    },
    onSuccess: () => {
      toast({
        title: "Producto actualizado",
        description: "El producto usado se ha actualizado exitosamente"
      });
      queryClient.invalidateQueries({ queryKey: ["marketplace-used-listings"] });
      queryClient.invalidateQueries({ queryKey: ["marketplace-listing", productId] });
      onComplete();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo actualizar el producto: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    }
  });

  // Función para manejar la adición de URLs de imágenes
  const handleAddImageUrl = () => {
    const currentImages = form.getValues("images") || [];
    form.setValue("images", [...currentImages, ""]);
  };

  // Función para manejar la eliminación de URLs de imágenes
  const handleRemoveImageUrl = (index: number) => {
    const currentImages = form.getValues("images") || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    form.setValue("images", newImages);

    // Actualizar también las previsualizaciones
    const newPreviews = [...imagePreviews];
    newPreviews.splice(index, 1);
    setImagePreviews(newPreviews);
  };

  // Función para eliminar imágenes locales
  const handleRemoveLocalImage = (index: number) => {
    setLocalImages(prev => {
      // Liberar URL de objeto
      URL.revokeObjectURL(prev[index].preview);
      // Eliminar la imagen del array
      return prev.filter((_, i) => i !== index);
    });
  };

  // Función para actualizar la previsualización de imágenes
  const updateImagePreview = (index: number, url: string) => {
    const newPreviews = [...imagePreviews];
    newPreviews[index] = url;
    setImagePreviews(newPreviews);
  };

  // Condiciones disponibles para productos usados
  const conditions = [
    { value: "excellent", label: "Excelente" },
    { value: "very_good", label: "Muy bueno" },
    { value: "good", label: "Bueno" },
    { value: "needs_repair", label: "Necesita reparación" }
  ];

  // Enviar el formulario
  async function onSubmit(data: UsedProductFormValues) {
    setIsLoading(true);
    try {
      if (productId) {
        await updateProductMutation.mutateAsync({ id: productId, data });
      } else {
        await createProductMutation.mutateAsync(data);
      }
    } finally {
      setIsLoading(false);
    }
  }

  // Mostrar estado de carga si se cargan datos del producto o categorías
  if ((isLoadingProduct && productId) || isLoadingCategories) {
    return <div className="p-4 text-center">Cargando datos...</div>;
  }

  return (
    <Card className="border-0 shadow-none w-full max-w-7xl mx-auto">
      <CardContent className="p-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Cambiar la estructura de las columnas para mejorar la distribución */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Columna 1: Información básica */}
              <div className="lg:col-span-1 space-y-4">
                <h3 className="text-lg font-medium mb-4">Información básica</h3>

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título del producto</FormLabel>
                      <FormControl>
                        <Input placeholder="Ej: Envasadora al vacío profesional" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Precio</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            value={field.value}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Año</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Año de fabricación"
                            {...field}
                            value={field.value === null ? '' : field.value}
                            onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Marca</FormLabel>
                        <FormControl>
                          <Input placeholder="Ej: WAAGE" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ubicación</FormLabel>
                        <FormControl>
                          <Input placeholder="Ej: Barcelona" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="condition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona un estado" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {conditions.map((condition) => (
                            <SelectItem key={condition.value} value={condition.value}>
                              {condition.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between">
                      <FormLabel>Producto activo</FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* --- Añadir Campo Select para Categoría --- */}
                <FormField
                  control={form.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoría</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoadingCategories || !categories}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={isLoadingCategories ? "Cargando categorías..." : "Selecciona una categoría"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories && categories.length > 0 ? (
                            categories.map((category) => (
                              <SelectItem key={category.id} value={String(category.id)}>
                                {category.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="-" disabled>No hay categorías disponibles</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* --- Fin Campo Select para Categoría --- */}
              </div>

              {/* Columna 2: Descripción y características */}
              <div className="lg:col-span-1 space-y-4">
                <h3 className="text-lg font-medium mb-4">Descripción y características</h3>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Descripción detallada del producto..."
                          className="min-h-[178px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="features"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Características principales</FormLabel>
                      <div className="space-y-2">
                        {field.value.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={feature}
                              onChange={(e) => {
                                const newFeatures = [...field.value];
                                newFeatures[index] = e.target.value;
                                field.onChange(newFeatures);
                              }}
                              placeholder="Ej: Alta capacidad de producción"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                const newFeatures = field.value.filter((_, i) => i !== index);
                                field.onChange(newFeatures);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => field.onChange([...field.value, ""])}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Añadir característica
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="technical_specs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Especificaciones técnicas</FormLabel>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                        {field.value.map((spec, index) => (
                          <div key={index} className="grid grid-cols-2 gap-2">
                            <Input
                              value={spec.label}
                              onChange={(e) => {
                                const newSpecs = [...field.value];
                                newSpecs[index] = { ...spec, label: e.target.value };
                                field.onChange(newSpecs);
                              }}
                              placeholder="Etiqueta (ej: Potencia)"
                            />
                            <div className="flex items-center gap-2">
                              <Input
                                value={spec.value}
                                onChange={(e) => {
                                  const newSpecs = [...field.value];
                                  newSpecs[index] = { ...spec, value: e.target.value };
                                  field.onChange(newSpecs);
                                }}
                                placeholder="Valor (ej: 2200W)"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const newSpecs = field.value.filter((_, i) => i !== index);
                                  field.onChange(newSpecs);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => field.onChange([...field.value, { label: "", value: "" }])}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Añadir especificación
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Columna 3: Imágenes */}
              <div className="lg:col-span-1 space-y-4">
                <h3 className="text-lg font-medium mb-4">Imágenes</h3>

                <FormField
                  control={form.control}
                  name="images"
                  render={({ field }) => (
                    <FormItem>
                      <div className="space-y-4">
                        {/* Zona para arrastrar y soltar imágenes */}
                        <div
                          {...getRootProps()}
                          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                            isDragActive
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-300 hover:border-primary/50'
                          }`}
                        >
                          <input {...getInputProps()} />
                          <div className="flex flex-col items-center justify-center gap-2 p-4">
                            <ImagePlus className="h-10 w-10 text-gray-400" />
                            <p className="text-sm text-gray-500">
                              {isDragActive
                                ? "Suelta las imágenes aquí..."
                                : "Arrastra y suelta imágenes aquí, o haz clic para seleccionarlas"
                              }
                            </p>
                            <p className="text-xs text-gray-400">
                              Formatos permitidos: JPEG, PNG, GIF, WEBP. Máximo 5MB por imagen.
                            </p>
                          </div>
                        </div>

                        {/* Vista previa de imágenes locales para subir */}
                        {localImages.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Imágenes para subir</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {localImages.map((image, index) => (
                                <div key={index} className="relative group">
                                  <div className="relative w-full h-24 rounded-md overflow-hidden border border-gray-200">
                                    <img
                                      src={image.preview}
                                      alt={`Imagen local ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                    {image.uploading && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                                      </div>
                                    )}
                                    {image.error && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-red-500/50">
                                        <p className="text-xs text-white font-medium px-1">Error</p>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveLocalImage(index)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    disabled={image.uploading}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Vista previa de imágenes ya guardadas */}
                        {imagePreviews.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Imágenes guardadas</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto pr-1">
                              {imagePreviews.map((url, index) => (
                                url && (
                                  <div key={index} className="relative group">
                                    <img
                                      src={url}
                                      alt={`Vista previa ${index + 1}`}
                                      className="w-full h-24 object-cover rounded-md border border-gray-300"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveImageUrl(index)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Campos para las URLs (alternativa manual) */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Añadir por URL (opcional)</p>
                          {field.value.map((url, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Input
                                value={url}
                                onChange={(e) => {
                                  const newUrls = [...field.value];
                                  newUrls[index] = e.target.value;
                                  field.onChange(newUrls);
                                  updateImagePreview(index, e.target.value);
                                }}
                                placeholder="URL de la imagen"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveImageUrl(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddImageUrl}
                            className="w-full"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Añadir URL de imagen
                          </Button>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onComplete}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading || uploadingImages}
              >
                {(isLoading || uploadingImages) ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {uploadingImages
                      ? "Subiendo imágenes..."
                      : productId
                        ? "Actualizar producto"
                        : "Crear producto"}
                  </span>
                ) : (
                  <>{productId ? "Actualizar producto" : "Crear producto"}</>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
